
import React, { useEffect, useState } from 'react';
import { authToken } from '../authToken';
import { API_BASE_URL } from '../config';

export default function CCPanel({ onClose }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [menuCardId, setMenuCardId] = useState(null);
  const [confirmDeleteCardId, setConfirmDeleteCardId] = useState(null);
  const [editCardId, setEditCardId] = useState(null);
  const [editAvailable, setEditAvailable] = useState('');
  const [editError, setEditError] = useState(null);

  const fetchCards = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/creditcards`, {
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch credit cards');
        return res.json();
      })
      .then(data => {
        setCards(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };
  useEffect(() => { fetchCards(); }, []);

  // Add card form state
  const [newName, setNewName] = useState('');
  const [newDepth, setNewDepth] = useState('');
  const [addError, setAddError] = useState(null);

  const handleAdd = () => {
    setAddError(null);
    const depthNum = Number(newDepth);
    if (!newName.trim() || !depthNum || depthNum <= 0) {
      setAddError('Podaj nazwę i dodatni limit.');
      return;
    }
    fetch(`${API_BASE_URL}/creditcards`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': authToken } : {})
      },
  body: JSON.stringify({ name: newName.trim(), depth: depthNum, availableLimit: depthNum })
    })
      .then(res => {
        if (!res.ok) throw new Error('Nie udało się dodać karty');
        return res.json();
      })
      .then(() => {
        setShowAddPanel(false);
        setNewName('');
        setNewDepth('');
        fetchCards();
      })
      .catch(err => {
        setAddError(err.message);
      });
  };

  // Calculate summary
  const totalDepth = cards.reduce((sum, c) => sum + (Number(c.depth) || 0), 0);
  const totalAvail = cards.reduce((sum, c) => sum + (Number(c.availableLimit) || 0), 0);
  const totalUsed = cards.reduce((sum, c) => sum + ((Number(c.depth) || 0) - (Number(c.availableLimit) || 0)), 0);
  const totalLoad = totalDepth ? Math.round((totalUsed / totalDepth) * 100) : 0;

  const normalizeName = (name) => {
    // Normalize weird whitespace (e.g. NBSP) and make sorting/display consistent.
    return String(name ?? '')
      .replace(/\u00A0/g, ' ')
      .replace(/[\s\u00A0]+/g, ' ')
      .trim();
  };

  const cardKey = (card) => {
    // Some backends may not return `id` immediately after creating an item.
    // If the key is missing/unstable, React can "pin" the new row at the bottom.
    // Fall back to a deterministic composite key.
    return String(
      card?.id ?? `${normalizeName(card?.name)}|${Number(card?.depth) || 0}|${Number(card?.availableLimit) || 0}`
    );
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: '24px 12px 18px 12px',
      boxShadow: '0 2px 18px rgba(0,0,0,0.22)',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
      minWidth: 0,
      width: '520px',
      maxWidth: '98vw',
      minHeight: 0,
      height: '650px',
      maxHeight: '96vh',
      overflow: 'auto',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 16, fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }}
        aria-label="Close"
      >✖️</button>
      <h2 style={{ marginBottom: 16 }}>Credit Cards</h2>
      {/* Summary section */}
      <div style={{
        display: 'flex',
        gap: 24,
        marginBottom: 18,
        fontSize: '1.08rem',
        fontWeight: 500,
        color: '#333',
        justifyContent: 'space-between',
        background: '#fffbe6',
        borderRadius: 8,
        padding: '10px 18px',
        border: '1px solid #fa0',
      }}>
        <span>Limit: <b>{totalDepth}</b></span>
        <span>Available: <b>{totalAvail}</b></span>
        <span>Used: <b>{totalUsed}</b></span>
        <span>Load: <b>{totalLoad}%</b></span>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button
          style={{
            flex: 1,
            padding: '10px 0',
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 8,
            border: '1px solid #fa0',
            background: '#fff8e6',
            color: '#a22',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onClick={() => setShowAddPanel(true)}
        >➕ Add</button>
      </div>
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>Error: {error}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
        <thead>
          <tr style={{ background: '#f8f8f8' }}>
            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600 }}>Name</th>
            <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600 }}>Limit</th>
            <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600 }}>Available</th>
            <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600 }}>Used</th>
            <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600 }}>Load</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {[...cards]
            .sort((a, b) => {
              const an = normalizeName(a?.name);
              const bn = normalizeName(b?.name);
              const byName = an.localeCompare(bn, 'pl', { sensitivity: 'base', numeric: true });
              if (byName !== 0) return byName;
              // Stable tie-breaker so React doesn't reshuffle equal names between renders.
              // Avoid treating missing ids as '' (which can make new items appear "stuck" at one end).
              return cardKey(a).localeCompare(cardKey(b), 'pl', { sensitivity: 'base', numeric: true });
            })
            .map((card) => {
            const used = (Number(card.depth) || 0) - (Number(card.availableLimit) || 0);
            const load = (Number(card.depth) ? Math.round((used / Number(card.depth)) * 100) : 0);
            return (
              <tr key={cardKey(card)} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '8px' }}>{normalizeName(card.name)}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{card.depth}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{card.availableLimit}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{used}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{load}%</td>
                <td style={{ padding: '8px', textAlign: 'center', position: 'relative' }}>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: 22,
                      cursor: 'pointer',
                      color: '#888',
                      marginLeft: 8
                    }}
                    aria-label="Menu"
                    onClick={e => {
                      e.stopPropagation();
                      setMenuCardId(card.id);
                    }}
                  >⋯</button>
                  {menuCardId === card.id && (
                    <div style={{
                      position: 'absolute',
                      background: '#fff',
                      border: '1px solid #eee',
                      borderRadius: 8,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                      padding: 12,
                      zIndex: 12000,
                      right: 24,
                      top: 40
                    }}>
                      <button style={{ display: 'block', width: '100%', padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#227' }} onClick={() => { setMenuCardId(null); setEditCardId(card.id); setEditAvailable(card.availableLimit); setEditError(null); }}>Edit</button>
                      <button style={{ display: 'block', width: '100%', padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#a22' }} onClick={() => { setMenuCardId(null); setConfirmDeleteCardId(card.id); }}>Delete</button>
                      <button style={{ display: 'block', width: '100%', padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#888' }} onClick={() => setMenuCardId(null)}>Cancel</button>
                    </div>
                  )}
                  {confirmDeleteCardId === card.id && (
                    <div style={{
                      position: 'fixed',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%,-50%)',
                      background: '#fff',
                      borderRadius: 12,
                      boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
                      padding: 28,
                      zIndex: 13000,
                      minWidth: 240,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 16
                    }}>
                      <div style={{ fontSize: '1.1rem', marginBottom: 8 }}>Delete card <b>{card.name}</b>?</div>
                      <div style={{ display: 'flex', gap: 16 }}>
                        <button
                          onClick={() => {
                            fetch(`${API_BASE_URL}/creditcards/${card.id}`, {
                              method: 'DELETE',
                              headers: {
                                ...(authToken ? { 'Authorization': authToken } : {})
                              }
                            })
                              .then(res => {
                                if (!res.ok) throw new Error('Failed to delete');
                                setConfirmDeleteCardId(null);
                                fetchCards();
                              })
                              .catch(err => {
                                setError(err.message);
                                setConfirmDeleteCardId(null);
                              });
                          }}
                          style={{
                            padding: '10px 24px',
                            fontSize: '1.05rem',
                            fontWeight: 600,
                            borderRadius: 8,
                            border: '1px solid #f66',
                            background: '#fff0f0',
                            color: '#a22',
                            cursor: 'pointer',
                            transition: 'background 0.2s'
                          }}
                        >Delete</button>
                        <button
                          onClick={() => setConfirmDeleteCardId(null)}
                          style={{
                            padding: '10px 24px',
                            fontSize: '1.05rem',
                            fontWeight: 600,
                            borderRadius: 8,
                            border: '1px solid #bbb',
                            background: '#f8f8f8',
                            color: '#555',
                            cursor: 'pointer',
                            transition: 'background 0.2s'
                          }}
                        >Cancel</button>
                      </div>
                    </div>
                  )}
      {/* Edit availableLimit dialog */}
      {editCardId !== null && cards.find(c => c.id === editCardId) && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
          padding: 28,
          zIndex: 13000,
          minWidth: 280,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16
        }}>
          <div style={{ fontSize: '1.1rem', marginBottom: 8 }}>Edit available limit for <b>{cards.find(c => c.id === editCardId).name}</b></div>
          <input
            type="number"
            placeholder="Available limit"
            value={editAvailable}
            onChange={e => setEditAvailable(e.target.value)}
            style={{ fontSize: '1.05rem', padding: '8px', borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
            min={0}
          />
          {editError && <div style={{ color: 'red', fontSize: '0.98rem' }}>{editError}</div>}
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button
              onClick={() => {
                const cardToEdit = cards.find(c => c.id === editCardId);
                if (!cardToEdit) {
                  setEditError('Card not found.');
                  return;
                }
                const valNum = Number(editAvailable);
                if (isNaN(valNum) || valNum < 0 || valNum > Number(cardToEdit.depth)) {
                  setEditError('Enter a valid available limit (0 to card limit).');
                  return;
                }
                fetch(`${API_BASE_URL}/creditcards/${cardToEdit.id}`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(authToken ? { 'Authorization': authToken } : {})
                  },
                  body: JSON.stringify({
                    name: cardToEdit.name,
                    depth: cardToEdit.depth,
                    availableLimit: valNum
                  })
                })
                  .then(res => {
                    if (!res.ok) throw new Error('Failed to update');
                    setEditCardId(null);
                    setEditAvailable('');
                    fetchCards();
                  })
                  .catch(err => {
                    setEditError(err.message);
                  });
              }}
              style={{ padding: '10px 24px', fontSize: '1.05rem', fontWeight: 600, borderRadius: 8, border: '1px solid #fa0', background: '#fffbe6', color: '#a22', cursor: 'pointer', transition: 'background 0.2s' }}
            >Save</button>
            <button
              onClick={() => { setEditCardId(null); setEditError(null); setEditAvailable(''); }}
              style={{ padding: '10px 24px', fontSize: '1.05rem', fontWeight: 600, borderRadius: 8, border: '1px solid #bbb', background: '#f8f8f8', color: '#555', cursor: 'pointer', transition: 'background 0.2s' }}
            >Cancel</button>
          </div>
        </div>
      )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!loading && cards.length === 0 && <div>No credit cards.</div>}
      {showAddPanel && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
          padding: 28,
          zIndex: 13000,
          minWidth: 280,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16
        }}>
          <div style={{ fontSize: '1.1rem', marginBottom: 8 }}>Add new card</div>
          <input
            type="text"
            placeholder="Card name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            style={{ fontSize: '1.05rem', padding: '8px', borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
            autoFocus
          />
          <input
            type="number"
            placeholder="Limit (e.g. 5000)"
            value={newDepth}
            onChange={e => setNewDepth(e.target.value)}
            style={{ fontSize: '1.05rem', padding: '8px', borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
            min={1}
          />
          {addError && <div style={{ color: 'red', fontSize: '0.98rem' }}>{addError}</div>}
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button
              onClick={handleAdd}
              style={{ padding: '10px 24px', fontSize: '1.05rem', fontWeight: 600, borderRadius: 8, border: '1px solid #fa0', background: '#fffbe6', color: '#a22', cursor: 'pointer', transition: 'background 0.2s' }}
            >Add</button>
            <button
              onClick={() => { setShowAddPanel(false); setAddError(null); setNewName(''); setNewDepth(''); }}
              style={{ padding: '10px 24px', fontSize: '1.05rem', fontWeight: 600, borderRadius: 8, border: '1px solid #bbb', background: '#f8f8f8', color: '#555', cursor: 'pointer', transition: 'background 0.2s' }}
            >Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
